import React, { useRef } from "react";
import {
  Controller,
  ControllerRenderProps,
  FieldValues,
  useFormContext,
} from "react-hook-form";
import { CSSProperties } from "react";
import stylesCss from "./DefaultCheckbox.module.scss";
import { classNames } from "primereact/utils";
import {
  IfFalse,
  IfTrue,
  RenderWhen,
} from "../../../../infrastructure/components/RenderWhen";
import { useAppContext } from "../../../../../AppContext";
import { AppTooltip } from "../../../tooltip/AppTooltip";

import { dvSize } from "../../../../infrastructure/utilities/viewportUnitsUtility";
interface IDefaultCheckboxProps {
  name: string;
  label: string | React.ReactNode;
  type?: "radio" | "checkbox";
  disabled?: boolean;
  className?: string;
  styles?: CSSProperties;
  required?: boolean;
  width?: number | string;
  tooltip?: string;
  maxWidth?: string | number;
  onChange?: (checked: boolean) => void;
  standalone?: boolean;
  checked?: boolean;
}

export const DefaultCheckbox = ({
  name,
  label,
  type = "checkbox",
  disabled = false,
  className,
  styles,
  required,
  width,
  tooltip,
  maxWidth,
  onChange,
  standalone = false,
  checked,
}: IDefaultCheckboxProps) => {
  const { isDesktopOrTablet } = useAppContext();
  const formContext = useFormContext();

  if (!width) {
    if (isDesktopOrTablet) {
      width = "600px";
    } else {
      width = dvSize("80dvw");
    }
  }

  const onStandaloneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e.target.checked);
    }
  };

  const onFieldChange = (
    field: ControllerRenderProps<FieldValues, string>,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    field.onChange(e.target.checked);

    if (onChange) {
      onChange(e.target.checked);
    }
  };

  if (standalone) {
    return (
      <div
        className={classNames(stylesCss.root, className)}
        style={{
          ...styles,
          width,
          maxWidth,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        <input
          type={type}
          id={name}
          checked={checked || false}
          onChange={onStandaloneChange}
          disabled={disabled}
          className={classNames(stylesCss.checkbox)}
        />

        <RenderWhen condition={typeof label === "string"}>
          <IfTrue>
            <label
              htmlFor={name}
              className={stylesCss.root_label}
              style={{
                textAlign: "left",
              }}
            >
              {required && <span style={{ color: "red" }}>*</span>}
              {label}
              <AppTooltip
                className={stylesCss.root_tooltip}
                content={tooltip}
              />
            </label>
          </IfTrue>
          <IfFalse>
            <span>{label}</span>
          </IfFalse>
        </RenderWhen>
      </div>
    );
  }

  if (!formContext) {
    throw new Error(
      "DefaultCheckbox must be used within a FormProvider when standalone is false"
    );
  }

  const inputRef = useRef<HTMLInputElement>(null);

  const handleRootClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    const target = e.target as HTMLElement;
    if (target === inputRef.current) return;
    if (target.tagName === "LABEL") return;
    inputRef.current?.click();
  };

  return (
    <Controller
      name={name}
      control={formContext.control}
      render={({ field, fieldState }) => (
        <div
          className={classNames(stylesCss.root, className)}
          style={{ ...styles, width, cursor: disabled ? "not-allowed" : "pointer" }}
          onClick={handleRootClick}
        >
          <input
            ref={inputRef}
            type={type}
            id={name}
            checked={field.value || false}
            onChange={(e) => onFieldChange(field, e)}
            disabled={disabled}
            className={stylesCss.checkbox}
          />

          <RenderWhen condition={typeof label === "string"}>
            <IfTrue>
              <label
                htmlFor={name}
                className={classNames(stylesCss.root_label, {
                  [stylesCss.error]: !!fieldState.error?.message,
                })}
                style={{
                  cursor: disabled ? "not-allowed" : "pointer",
                  textAlign: "left",
                }}
              >
                {required && <span style={{ color: "red" }}>*</span>}
                {label}
                <AppTooltip
                  className={stylesCss.root_tooltip}
                  content={tooltip}
                />
              </label>
            </IfTrue>
            <IfFalse>
              <span
                className={classNames({
                  [stylesCss.error]: !!fieldState.error?.message,
                })}
              >
                {label}
              </span>
            </IfFalse>
          </RenderWhen>
        </div>
      )}
    />
  );
};
